/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import {
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

// Store
import useStore from '@store/index';
import { useElementSize } from '@mantine/hooks';
import {
	TApiResponseBrandPopulated,
	TParsedBrand,
} from '../types';
import { TApiError, TBrand } from '@global-types/api/api';
import {
	TParams,
	TSelectOption,
	TSortStatus,
} from '@global-types/app/app';
import { UseMutationResult } from '@tanstack/react-query';
import {
	cloneDeep,
	removeEmptyArrays,
} from '@utils/functions';
import { TMeasurabilityProjectQueryParams } from '@features/catalogs/meausrability-projects/list/types';
import { TQueryParams } from '@global-types/index';
import { ACLS } from '@config/constants/acls';
import { useAuthorization } from '@lib/authorization';
import { ROLES } from '@config/constants/roles';

/* -------------------------------------------------------------------------- */
/*                                  Hook                                      */
/* -------------------------------------------------------------------------- */
export const useBrands = () => {
	/**************************** Refs ****************************/
	const containerRef = useRef<HTMLDivElement | null>(
		null
	);

	/**************************** States ****************************/
	const [resetFilterBrands, setResetFilterBrands] =
		useState(false);
	const [openDrawer, setOpenDrawer] = useState(false);
	const [openDrawerAddBrand, setOpenDrawerAddBrand] =
		useState(false);

	const [openDrawerEditBrand, setOpenDrawerEditBrand] =
		useState(false);

	const [showScrollFilters, setShowScrollFilters] =
		useState(false);
	const [
		openSelectedItemsToDeleteModal,
		setOpenSelectedItemsToDeleteModal,
	] = useState(false);

	const [isOverflowFilters] = useState(false);

	const {
		brandsPage,
		brandsPageSize,
		setBrandsPage,
		setBrandsSelectedRecords,
		setBrandsOpenConfirmationModalDelete,
		BrandsQueryParams,
		setBrandsQueryParams,
		brandsSortStatus,
		setBrandsSortStatus,
		setBrandsSortParams,
		brandsSortParams,
		setBrandsPageSize,
		setBrandsSelectedRow,
		brandsLastAccessTokenCreated,
		setBrandsLastAccessTokenCreated,
		brandsDatatableOptions,
		setBrandsDatatableOptions,
		brandsQueryParams,
		brandsSelectedRow,
		brandsOpenConfirmationModalDelete,
		brandsSelectedRecords,
		setBrandsWithoutContactDatatableOptions,
		brandsWithoutContactDatatableOptions,
	} = useStore();
	const { checkAcl, checkAccess } = useAuthorization();

	const { ref, width: widthContainer } = useElementSize();
	/**************************** Drawer show more production ****************************/
	const handleAddItem = () => {
		setOpenDrawerAddBrand(true);
	};

	const handleDeleteMultipleItems = () => {
		setOpenSelectedItemsToDeleteModal(true);
	};

	/**************************** Reset ****************************/
	const handleReset = () => {
		setBrandsPageSize(10);
		setBrandsQueryParams({});
		setBrandsLastAccessTokenCreated(null);
		setBrandsPage(1);
		setBrandsSortParams('-createdAt');
		setBrandsSortStatus({});
	};

	useEffect(() => {
		return () => {
			handleReset();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useLayoutEffect(() => {
		if (
			containerRef.current &&
			containerRef.current.clientWidth <
				containerRef.current.scrollWidth
		) {
			setShowScrollFilters(true);
		} else {
			setShowScrollFilters(false);
		}
	}, [widthContainer]);

	/**************************** Actions ****************************/

	const handleCloseAddBrandDrawer = () => {
		setOpenDrawerAddBrand(false);
	};

	const handleCloseEditBrandDrawer = () => {
		setOpenDrawerEditBrand(false);
	};

	const handleCloseShowMoreBrandDrawer = () => {
		setOpenDrawer(false);
	};

	const handleEditBrandDrawer = (row: TParsedBrand) => {
		handleEdit(row);
		setOpenDrawer(false);
	};

	const handleEdit = (row: any) => {
		setBrandsSelectedRow(row);
		setOpenDrawerEditBrand(true);
	};

	const handleChangeObfuscateIPFS = (
		row: any,
		obfuscateIPFS: any
	) => {
		obfuscateIPFS.mutate({
			id: row.id,
			obfuscateIPFS: !row.obfuscateIPFS,
		});
	};

	const handleCountryFilter = (
		_countries: TSelectOption[]
	) => {
		setBrandsPage(1);

		const countries = _countries.map(
			(_country: TSelectOption) => _country.value
		);

		if (countries.length == 0) {
			const _offsetMesurabilityProjectsQueryParams =
				cloneDeep(brandsQueryParams);
			delete _offsetMesurabilityProjectsQueryParams[
				'location.countryCode'
			];
			setBrandsQueryParams(
				_offsetMesurabilityProjectsQueryParams
			);
			return;
		}
		const queryParamsCountries = {
			'location.countryCode': {
				$in: countries,
			},
		};
		const filterParams: TMeasurabilityProjectQueryParams =
			removeEmptyArrays({
				...brandsQueryParams,
				...queryParamsCountries,
			});
		setBrandsQueryParams(filterParams);
	};

	const handleScroll = (scrollTo: string) => {
		if (containerRef.current) {
			if (scrollTo === 'right') {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft +
					containerRef.current.offsetWidth;
			} else {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft -
					containerRef.current.offsetWidth;
			}
		}
	};
	const handleSortParams = (_column: TSortStatus) => {
		let sortParams = _column.columnAccessor;

		switch (_column.columnAccessor) {
			case 'brand':
				sortParams = 'entity';
				break;
			case 'contactName':
				sortParams = 'contact.firstName';
				break;
			case 'contactEmail':
				sortParams = 'contact.email';
				break;
			case 'place':
				sortParams = 'location.place';
				break;
			case 'contactPhone':
				sortParams = 'contact.phone';
				break;
			case 'countryCode':
				sortParams = 'location.countryCode';
				break;
			default:
				sortParams;
				break;
		}

		if (_column.direction == 'asc') {
			setBrandsSortParams(sortParams);
		} else {
			setBrandsSortParams('-' + sortParams);
		}
	};

	const handleOrganizationFilter = (
		organizations: TSelectOption[]
	) => {
		setBrandsSelectedRecords([]);
		setBrandsPage(1);
		const queryParamsProjects = {
			organization: organizations.map(
				(_organization: TSelectOption) =>
					_organization.value
			),
		};

		setBrandsQueryParams({
			...BrandsQueryParams,
			...queryParamsProjects,
		});
	};

	/*************************************** Delete one Item  ***************************************/

	const handleConfirmDeleteItem = (
		remove: UseMutationResult<
			TApiResponseBrandPopulated,
			TApiError,
			string,
			unknown
		>
	) => {
		setBrandsOpenConfirmationModalDelete({
			open: false,
			params: {},
		});

		remove.mutate(
			brandsOpenConfirmationModalDelete.params.id
		);
		setOpenDrawer(false);
		setBrandsSelectedRecords([]);
		setBrandsLastAccessTokenCreated(null);
	};

	const handleCloseConfirmationDeleteItem = () => {
		setBrandsOpenConfirmationModalDelete({
			open: false,
			params: {},
		});
	};

	const handleOpenConfirmationDeleteItem = () => {
		setBrandsOpenConfirmationModalDelete({
			open: true,
			params: brandsSelectedRow,
		});
	};

	const handleapplystatusfilter = (status: any) => {
		setBrandsSelectedRecords([]);
		setBrandsPage(1);
		const queryParamsVerified = {
			verified: status.map(
				(_status: any) => _status.value
			),
		};
		setBrandsQueryParams({
			...brandsQueryParams,
			...queryParamsVerified,
		});
	};

	const handleapplyipfsstatusfilter = (status: any) => {
		setBrandsSelectedRecords([]);
		setBrandsPage(1);
		if (
			status &&
			Array.isArray(status) &&
			status.length > 0
		) {
			const queryParamsVerified = {
				obfuscateIPFS: status[0].value,
			};
			setBrandsQueryParams({
				...brandsQueryParams,
				...queryParamsVerified,
			});
		} else {
			const { obfuscateIPFS, ...rest } =
				brandsQueryParams;
			setBrandsQueryParams({
				...rest,
			});
		}
	};

	/*************************************** Datatables  ***************************************/
	const handleViewMore = (row: TParsedBrand) => {
		setBrandsSelectedRow(row);
		setOpenDrawer(true);
	};

	const handleRowClicked = (row: { record: TBrand }) => {
		if (
			checkAcl(ACLS.BRANDS.READ) &&
			checkAccess({
				allowedRoles: [
					ROLES.ADMIN,
					ROLES.SUPERADMIN,
					ROLES.STAFF,
					ROLES.ORGANIZATION_ADMIN,
					ROLES.ORGANIZATION_STAFF,
				],
			})
		) {
			setBrandsSelectedRow(row.record);
			setOpenDrawer(true);
		}
	};

	const handleSelectedRecordsChange = (
		selectedRecords: TBrand
	) => {
		setBrandsSelectedRecords(selectedRecords);
	};

	const handleSortStatusChange = (data: TSortStatus) => {
		handleSortParams(data);
		setBrandsSortStatus(data);
	};

	/**************************** Pagination ****************************/

	const handlepaginationchangesize = function (
		_size: string | number
	) {
		setBrandsPageSize(_size);
		setBrandsPage(1);
	};

	const handlechangepagination = (data: number) => {
		setBrandsPage(data);
	};

	const handleResetSelectedItems = () => {
		setBrandsSelectedRecords([]);
	};

	const handleBrandFilter = (brands: TQueryParams[]) => {
		setBrandsPage(1);
		const queryParamsProjects = {
			entity: brands.map(
				(_brand: TParams) => _brand.value
			),
		};

		setBrandsQueryParams({
			...brandsQueryParams,
			...queryParamsProjects,
		});
	};

	const handleTechnologyFilter = (
		_technologies: TSelectOption[]
	) => {
		setBrandsPage(1);
		const queryParamsProjects = {
			technology: _technologies.map(
				(_technology: TSelectOption) => _technology.value
			),
		};

		const filterParams: TMeasurabilityProjectQueryParams =
			removeEmptyArrays({
				...brandsQueryParams,
				...queryParamsProjects,
			});
		setBrandsQueryParams(filterParams);
	};

	/********************************************************************************************/

	return {
		handleResetSelectedItems,
		handlepaginationchangesize,
		handlechangepagination,
		handleSortStatusChange,
		handleSelectedRecordsChange,
		handleRowClicked,
		handleViewMore,
		handleOpenConfirmationDeleteItem,
		handleCloseConfirmationDeleteItem,
		handleConfirmDeleteItem,
		openSelectedItemsToDeleteModal,
		setOpenSelectedItemsToDeleteModal,
		showScrollFilters,
		setShowScrollFilters,
		openDrawerAddBrand,
		setOpenDrawerAddBrand,
		openDrawer,
		setOpenDrawer,
		handleOrganizationFilter,
		handleScroll,
		handleSortParams,
		containerRef,
		handleAddItem,
		handleDeleteMultipleItems,
		handleReset,
		ref,
		widthContainer,
		isOverflowFilters,
		brandsPage,
		brandsPageSize,
		brandsSelectedRecords,
		setBrandsSelectedRecords,
		brandsOpenConfirmationModalDelete,
		setBrandsOpenConfirmationModalDelete,
		brandsQueryParams,
		brandsSortStatus,
		brandsSortParams,
		brandsSelectedRow,
		brandsLastAccessTokenCreated,
		setBrandsLastAccessTokenCreated,
		brandsDatatableOptions,
		setBrandsDatatableOptions,
		handleCountryFilter,
		resetFilterBrands,
		setResetFilterBrands,
		handleBrandFilter,
		handleTechnologyFilter,
		handleapplystatusfilter,
		handleapplyipfsstatusfilter,
		handleChangeObfuscateIPFS,
		openDrawerEditBrand,
		setOpenDrawerEditBrand,
		handleEdit,
		handleCloseAddBrandDrawer,
		handleCloseEditBrandDrawer,
		handleCloseShowMoreBrandDrawer,
		handleEditBrandDrawer,
		setBrandsWithoutContactDatatableOptions,
		brandsWithoutContactDatatableOptions,
	};
};
